import { useContext, useId } from "react";
import { Collection } from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./DesktopTabList.styled";
import { DEFAULT_DESKTOP_HEADING_LEVEL } from "../Tabs.constants";
import TabsContext from "../TabsContext";
import { Typography } from "../../Typography";

function DesktopTabList() {
  const {
    items,
    desktopHeading,
    desktopHeadingLevel = DEFAULT_DESKTOP_HEADING_LEVEL,
    testID,
  } = useContext(TabsContext);

  const tokens = useTabsDesignTokens();

  const { headingID, getTabA11yData } = useA11yData();

  return (
    <S.Container>
      {desktopHeading && (
        <Typography
          id={headingID}
          component={`h${desktopHeadingLevel}`}
          variant={tokens.heading.variant}
          fontFamily={tokens.heading.fontFamily}
          testID={`${testID}-heading`}
        >
          {desktopHeading}
        </Typography>
      )}

      <S.TabList aria-labelledby={headingID} data-testid={`${testID}-items`}>
        <Collection items={items}>
          {(item) => {
            const withIcon = Boolean(item.icon);
            const isLastTab = item === items.at(-1);

            const { labelID, secondaryLabelID } = getTabA11yData(item.id);

            return (
              <S.Tab
                id={item.id}
                isDisabled={item.isDisabled}
                aria-labelledby={`${labelID} ${secondaryLabelID}`}
                data-testid={`${testID}-item-${item.id}`}
              >
                <S.TabContentContainer>
                  <S.TabContent $withIcon={withIcon}>
                    <div>
                      <S.TabLabelWrapper>
                        <Typography
                          id={labelID}
                          component="span"
                          variant={tokens.tab.label.variant}
                          fontFamily={tokens.tab.label.fontFamily}
                          color="inherit"
                        >
                          {item.label}
                        </Typography>
                      </S.TabLabelWrapper>

                      {item.secondaryLabel && (
                        <S.TabSecondaryLabelWrapper>
                          <Typography
                            id={secondaryLabelID}
                            component="span"
                            variant={tokens.tab.secondaryLabel.variant}
                            fontFamily={tokens.tab.secondaryLabel.fontFamily}
                            color="inherit"
                          >
                            {item.secondaryLabel}
                          </Typography>
                        </S.TabSecondaryLabelWrapper>
                      )}
                    </div>

                    {withIcon && (
                      <S.TabIconWrapper>{item.icon}</S.TabIconWrapper>
                    )}
                  </S.TabContent>

                  <S.TabFocusRing />
                </S.TabContentContainer>

                {!isLastTab && <S.Separator />}
              </S.Tab>
            );
          }}
        </Collection>
      </S.TabList>
    </S.Container>
  );
}

function useTabsDesignTokens() {
  const { tokens } = useDesignTokens();
  const { heading, tab } = tokens.tabs.desktop;

  return {
    heading: {
      variant: heading.typography.variant,
      fontFamily: heading.typography.fontFamily,
    },
    tab: {
      label: {
        variant: tab.label.typography.variant,
        fontFamily: tab.label.typography.fontFamily,
      },
      secondaryLabel: {
        variant: tab.secondaryLabel.typography.variant,
        fontFamily: tab.secondaryLabel.typography.fontFamily,
      },
    },
  };
}

function useA11yData() {
  const instanceID = useId();

  const headingID = `${instanceID}-heading`;

  function getTabA11yData(id: string) {
    const labelID = `${instanceID}-tab-label-${id}`;
    const secondaryLabelID = `${instanceID}-tab-secondary-label-${id}`;

    return { labelID, secondaryLabelID };
  }

  return { headingID, getTabA11yData };
}

export default DesktopTabList;
